import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { RotateCcw, Save, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = [['manager', 'Manager'], ['staff', 'Staff'], ['supplier', 'Supplier']];

// Ticking a permission also ticks what it needs; unticking removes anything that depends on it.
export const togglePerm = (catalog, current, key, on) => {
  const implies = Object.fromEntries(catalog.map((c) => [c.key, c.implies]));
  const set = new Set(current);
  if (on) {
    const stack = [key];
    while (stack.length) { const k = stack.pop(); if (!set.has(k)) { set.add(k); stack.push(...(implies[k] || [])); } }
  } else {
    const stack = [key];
    while (stack.length) {
      const k = stack.pop();
      set.delete(k);
      catalog.forEach((c) => { if (set.has(c.key) && c.implies.includes(k)) stack.push(c.key); });
    }
  }
  return catalog.map((c) => c.key).filter((k) => set.has(k));
};

export const PermissionMatrix = ({ catalog, columns, values, onToggle, testPrefix }) => {
  const areas = [...new Set(catalog.map((c) => c.area))];
  const needs = (c) => c.implies.map((k) => catalog.find((x) => x.key === k)?.label).filter(Boolean);
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b">
          <th className="text-left py-2 pr-4 font-medium">Permission</th>
          {columns.map(([k, label]) => <th key={k} className="py-2 px-3 font-medium text-center w-28">{label}</th>)}
        </tr>
      </thead>
      <tbody>
        {areas.map((area) => (
          <React.Fragment key={area}>
            <tr><td colSpan={columns.length + 1} className="pt-4 pb-1 text-xs uppercase tracking-wide text-muted-foreground font-semibold">{area}</td></tr>
            {catalog.filter((c) => c.area === area).map((c) => (
              <tr key={c.key} className="border-b border-border/50 hover:bg-muted/30">
                <td className="py-2 pr-4">
                  <p>{c.label}</p>
                  {c.implies.length > 0 && <p className="text-xs text-muted-foreground">Also gives: {needs(c).join(', ')}</p>}
                </td>
                {columns.map(([k]) => (
                  <td key={k} className="text-center">
                    <Checkbox checked={(values[k] || []).includes(c.key)} onCheckedChange={(v) => onToggle(k, c.key, !!v)}
                      data-testid={`${testPrefix}-${k}-${c.key}`} />
                  </td>
                ))}
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
};

export default function RolesPermissions() {
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState({});
  const [defaults, setDefaults] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/permissions`, { withCredentials: true }).then((r) => {
      setCatalog(r.data.catalog); setRoles(r.data.roles); setDefaults(r.data.defaults);
    }).catch(() => toast.error('Failed to load permissions'));
  }, []);

  const toggle = (role, key, on) => {
    setRoles((r) => ({ ...r, [role]: togglePerm(catalog, r[role] || [], key, on) }));
    setDirty((d) => ({ ...d, [role]: true }));
  };

  const resetDefaults = () => {
    setRoles(Object.fromEntries(ROLES.map(([k]) => [k, (defaults[k] || []).reduce((acc, p) => togglePerm(catalog, acc, p, true), [])])));
    setDirty({ manager: true, staff: true, supplier: true });
  };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(Object.keys(dirty).map((role) =>
        axios.put(`${API}/permissions/${role}`, { permissions: roles[role] }, { withCredentials: true })));
      setDirty({});
      toast.success('Permissions saved. Users get the new access the next time a page loads.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save permissions'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="roles-permissions-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}><ShieldCheck className="w-7 h-7" />Roles & Permissions</h1>
        <p className="text-muted-foreground mt-1">Master Admin always has full access. Branch and supplier access, and per-user overrides, are set on each user in Settings &gt; Users.</p>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Role permissions</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetDefaults} data-testid="roles-reset-btn"><RotateCcw className="w-4 h-4 mr-2" />Defaults</Button>
            <Button onClick={save} disabled={saving || !Object.keys(dirty).length} data-testid="roles-save-btn"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <PermissionMatrix catalog={catalog} columns={ROLES} values={roles} onToggle={toggle} testPrefix="perm" />
        </CardContent>
      </Card>
    </div>
  );
}
